import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ConfigFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    firstNameLabelDe: string;
    firstNameLabelEn: string;
    lastNameLabelDe: string;
    lastNameLabelEn: string;
    emailLabelDe: string;
    emailLabelEn: string;
    phoneLabelDe: string;
    phoneLabelEn: string;
    messageLabelDe: string;
    messageLabelEn: string;
    termsCheckboxTextDe: string;
    termsCheckboxTextEn: string;
    newsletterCheckboxTextDe: string;
    newsletterCheckboxTextEn: string;
    continueButtonTextDe: string;
    continueButtonTextEn: string;
    // New fields
    pageSubtitleDe: string;
    pageSubtitleEn: string;
    firstNamePlaceholderDe: string;
    firstNamePlaceholderEn: string;
    lastNamePlaceholderDe: string;
    lastNamePlaceholderEn: string;
    emailPlaceholderDe: string;
    emailPlaceholderEn: string;
    phonePlaceholderDe: string;
    phonePlaceholderEn: string;
    wantEarlierTimeslotLabelDe: string;
    wantEarlierTimeslotLabelEn: string;
    preferredContactTimesLabelDe: string;
    preferredContactTimesLabelEn: string;
    contactTimeMorningDe: string;
    contactTimeMorningEn: string;
    contactTimeAfternoonDe: string;
    contactTimeAfternoonEn: string;
    contactTimeEveningDe: string;
    contactTimeEveningEn: string;
};

const EnterDetailsCMS = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        firstNameLabelDe: '',
        firstNameLabelEn: '',
        lastNameLabelDe: '',
        lastNameLabelEn: '',
        emailLabelDe: '',
        emailLabelEn: '',
        phoneLabelDe: '',
        phoneLabelEn: '',
        messageLabelDe: '',
        messageLabelEn: '',
        termsCheckboxTextDe: '',
        termsCheckboxTextEn: '',
        newsletterCheckboxTextDe: '',
        newsletterCheckboxTextEn: '',
        continueButtonTextDe: '',
        continueButtonTextEn: '',
        // New fields
        pageSubtitleDe: '',
        pageSubtitleEn: '',
        firstNamePlaceholderDe: '',
        firstNamePlaceholderEn: '',
        lastNamePlaceholderDe: '',
        lastNamePlaceholderEn: '',
        emailPlaceholderDe: '',
        emailPlaceholderEn: '',
        phonePlaceholderDe: '',
        phonePlaceholderEn: '',
        wantEarlierTimeslotLabelDe: '',
        wantEarlierTimeslotLabelEn: '',
        preferredContactTimesLabelDe: '',
        preferredContactTimesLabelEn: '',
        contactTimeMorningDe: '',
        contactTimeMorningEn: '',
        contactTimeAfternoonDe: '',
        contactTimeAfternoonEn: '',
        contactTimeEveningDe: '',
        contactTimeEveningEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getEnterDetailsConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateEnterDetailsConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getEnterDetailsConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                firstNameLabelDe: config.firstNameLabelDe,
                firstNameLabelEn: config.firstNameLabelEn,
                lastNameLabelDe: config.lastNameLabelDe,
                lastNameLabelEn: config.lastNameLabelEn,
                emailLabelDe: config.emailLabelDe,
                emailLabelEn: config.emailLabelEn,
                phoneLabelDe: config.phoneLabelDe,
                phoneLabelEn: config.phoneLabelEn,
                messageLabelDe: config.messageLabelDe,
                messageLabelEn: config.messageLabelEn,
                termsCheckboxTextDe: config.termsCheckboxTextDe,
                termsCheckboxTextEn: config.termsCheckboxTextEn,
                newsletterCheckboxTextDe: config.newsletterCheckboxTextDe ?? '',
                newsletterCheckboxTextEn: config.newsletterCheckboxTextEn ?? '',
                continueButtonTextDe: config.continueButtonTextDe,
                continueButtonTextEn: config.continueButtonTextEn,
                // New fields
                pageSubtitleDe: config.pageSubtitleDe ?? '',
                pageSubtitleEn: config.pageSubtitleEn ?? '',
                firstNamePlaceholderDe: config.firstNamePlaceholderDe ?? '',
                firstNamePlaceholderEn: config.firstNamePlaceholderEn ?? '',
                lastNamePlaceholderDe: config.lastNamePlaceholderDe ?? '',
                lastNamePlaceholderEn: config.lastNamePlaceholderEn ?? '',
                emailPlaceholderDe: config.emailPlaceholderDe ?? '',
                emailPlaceholderEn: config.emailPlaceholderEn ?? '',
                phonePlaceholderDe: config.phonePlaceholderDe ?? '',
                phonePlaceholderEn: config.phonePlaceholderEn ?? '',
                wantEarlierTimeslotLabelDe: config.wantEarlierTimeslotLabelDe ?? '',
                wantEarlierTimeslotLabelEn: config.wantEarlierTimeslotLabelEn ?? '',
                preferredContactTimesLabelDe: config.preferredContactTimesLabelDe ?? '',
                preferredContactTimesLabelEn: config.preferredContactTimesLabelEn ?? '',
                contactTimeMorningDe: config.contactTimeMorningDe ?? '',
                contactTimeMorningEn: config.contactTimeMorningEn ?? '',
                contactTimeAfternoonDe: config.contactTimeAfternoonDe ?? '',
                contactTimeAfternoonEn: config.contactTimeAfternoonEn ?? '',
                contactTimeEveningDe: config.contactTimeEveningDe ?? '',
                contactTimeEveningEn: config.contactTimeEveningEn ?? '',
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Daten-Eingabe CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie die Texte und Beschriftungen für die Kundendaten-Eingabe-Seite.
                </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='flex justify-end'>
                    <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                        {updateMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                            <Save className='mr-2 h-4 w-4' />
                        )}
                        Speichern
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Seiten-Header</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Seitentitel (DE)', 'Page Title (EN)', 'pageTitleDe', 'pageTitleEn')}
                        {renderFieldPair('Seiten-Untertitel (DE)', 'Page Subtitle (EN)', 'pageSubtitleDe', 'pageSubtitleEn', true)}
                        {renderFieldPair('Fortschrittsanzeige (DE)', 'Progress Display (EN)', 'progressDisplayDe', 'progressDisplayEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Formular-Labels</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Vorname (DE)', 'First Name (EN)', 'firstNameLabelDe', 'firstNameLabelEn')}
                        {renderFieldPair('Nachname (DE)', 'Last Name (EN)', 'lastNameLabelDe', 'lastNameLabelEn')}
                        {renderFieldPair('E-Mail (DE)', 'Email (EN)', 'emailLabelDe', 'emailLabelEn')}
                        {renderFieldPair('Telefon (DE)', 'Phone (EN)', 'phoneLabelDe', 'phoneLabelEn')}
                        {renderFieldPair('Nachricht (DE)', 'Message (EN)', 'messageLabelDe', 'messageLabelEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Formular-Platzhalter</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Vorname Platzhalter (DE)', 'First Name Placeholder (EN)', 'firstNamePlaceholderDe', 'firstNamePlaceholderEn')}
                        {renderFieldPair('Nachname Platzhalter (DE)', 'Last Name Placeholder (EN)', 'lastNamePlaceholderDe', 'lastNamePlaceholderEn')}
                        {renderFieldPair('E-Mail Platzhalter (DE)', 'Email Placeholder (EN)', 'emailPlaceholderDe', 'emailPlaceholderEn')}
                        {renderFieldPair('Telefon Platzhalter (DE)', 'Phone Placeholder (EN)', 'phonePlaceholderDe', 'phonePlaceholderEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Früherer Termin & Kontaktzeiten</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Früherer Termin Label (DE)', 'Earlier Timeslot Label (EN)', 'wantEarlierTimeslotLabelDe', 'wantEarlierTimeslotLabelEn', true)}
                        {renderFieldPair('Bevorzugte Kontaktzeiten Label (DE)', 'Preferred Contact Times Label (EN)', 'preferredContactTimesLabelDe', 'preferredContactTimesLabelEn')}
                        {renderFieldPair('Vormittag (DE)', 'Morning (EN)', 'contactTimeMorningDe', 'contactTimeMorningEn')}
                        {renderFieldPair('Nachmittag (DE)', 'Afternoon (EN)', 'contactTimeAfternoonDe', 'contactTimeAfternoonEn')}
                        {renderFieldPair('Abend (DE)', 'Evening (EN)', 'contactTimeEveningDe', 'contactTimeEveningEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>AGB & Datenschutz</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('AGB Checkbox Text (DE)', 'Terms Checkbox Text (EN)', 'termsCheckboxTextDe', 'termsCheckboxTextEn', true)}
                        {renderFieldPair('Newsletter Checkbox Text (DE)', 'Newsletter Checkbox Text (EN)', 'newsletterCheckboxTextDe', 'newsletterCheckboxTextEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Weiter-Button</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Weiter-Button (DE)', 'Continue Button (EN)', 'continueButtonTextDe', 'continueButtonTextEn')}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default EnterDetailsCMS;
